/* WeQuote Dev Board — My Board (v2 §4, verify §72/§73). A per-developer
 * view: "mine" = the card's assignee OR a stage assigned to me, so a staged
 * card whose stage I own shows on my board even when the card sits in
 * someone else's column. */
import type { Feature, Stage } from './types'
import { isProgress } from './constants'
import { isDevOverdue, isDesignOverdue, isDesignStartSoon } from './logic'
import { datedCard, rmAtRisk } from './schedule'
import { activeStageIndex } from './stages'
import { sectionRank } from './logic'
import { calDaysISO } from './calendar'

export function isMine(f: Feature, dev: string): boolean {
  return f.dev === dev || (f.multiStage && (f.stages || []).some((s) => (s.dev || f.dev) === dev))
}
export interface StageRef { f: Feature; s: Stage; i: number }
/** The ACTIVE (first not-done) stage of each staged card, when it's mine. */
export function myActiveStages(features: Feature[], dev: string): StageRef[] {
  const out: StageRef[] = []
  features.forEach((f) => {
    if (!f.multiStage) return
    const ai = activeStageIndex(f)
    if (ai > -1) {
      const s = f.stages[ai]
      if ((s.dev || f.dev) === dev) out.push({ f, s, i: ai })
    }
  })
  return out
}
export interface MyUnit { f: Feature; stage?: StageRef; sStart?: string }
export interface MyBoard {
  nowItems: MyUnit[]
  nextToday: MyUnit[]
  thisWeek: MyUnit[]
  upnext: Feature[]
  done: Feature[]
  tiles: { designOverdue: number; devOverdue: number; atRisk: number; designSoon: number }
}
export function buildMyBoard(features: Feature[], dev: string, schedStart: Record<number, string>, today: string): MyBoard {
  const mine = features.filter((f) => isMine(f, dev))
  const stages = myActiveStages(features, dev)
  const wkEnd = calDaysISO(today, 6)
  const nowItems: MyUnit[] = []
  // Whole-card rows: non-staged cards I own. Staged work appears via my
  // ACTIVE stage only — a finished stage means I'm not working on it now.
  features.filter((f) => f.dev === dev && !f.multiStage && isProgress(f.status) && f.status !== 'live')
    .sort((a, b) => a.po - b.po).forEach((f) => nowItems.push({ f }))
  stages.forEach((u) => { if (u.s.schedStart && u.s.schedStart <= today) nowItems.push({ f: u.f, stage: u }) })
  const future: MyUnit[] = []
  mine.forEach((f) => {
    if (!isProgress(f.status) && f.status !== 'live' && !f.multiStage && datedCard(f) && schedStart[f.id]) future.push({ f, sStart: schedStart[f.id] })
  })
  stages.forEach((u) => { if (u.s.schedStart && u.s.schedStart > today) future.push({ f: u.f, stage: u, sStart: u.s.schedStart }) })
  return {
    nowItems,
    nextToday: future.filter((u) => u.sStart === today),
    thisWeek: future.filter((u) => u.sStart! > today && u.sStart! <= wkEnd).sort((a, b) => (a.sStart! < b.sStart! ? -1 : 1)),
    upnext: mine.filter((f) => f.status === 'now' || f.status === 'next').sort((a, b) => sectionRank(a) - sectionRank(b) || a.po - b.po),
    done: mine.filter((f) => f.status === 'live').sort((a, b) => (b.completedOn || '').localeCompare(a.completedOn || '')),
    tiles: {
      designOverdue: mine.filter(isDesignOverdue).length,
      devOverdue: mine.filter(isDevOverdue).length,
      atRisk: mine.filter((f) => rmAtRisk(f) && !f.riskAcknowledged).length,
      designSoon: mine.filter(isDesignStartSoon).length,
    },
  }
}
